/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React from "react";
import { simpleFetch } from "../../../restWrapper";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import errorMsg from "../../../assets/json/errMsg.json";
import { validOrgInfo } from "../../../assets/validators/organizations_orgInfo.js";
import { Textbox } from "../../../assets/component/Textbox";
import PropTypes from "prop-types";

import "react-phone-number-input/style.css";
import PhoneInput, {
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import {
  COMPANY_NAME,
  MAIN_DOMAIN,
  DEFAULT_PAYMENT_MINIMUM_LIMIT,
  PLATFORM,
} from "../../../config";

const orderOfInputField = [
  "organizationName",
  "organizationAddress",
  "organizationIRDNo",
  "subDomain",
  "email",
  "lastName",
  "firstName",
  "phoneNumber",
  "defaultLanguage",
  "minimumPaymentAmount",
  "organizationUrl",
  "statementDescriptor",
];

export function RenderOrgInfo(state, func, textObj) {
  if (!state || !func) return;

  const logoStyle = {
    backgroundImage: "url('" + state.organizationObj.logo + "')",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    border: "none",
  };

  function clickSubmit(e) {
    var bool = true;
    const err = errorMsg[func.state.language].orgInfoErr;
    var errData = state.errMsgInfo;
    if (state.orgInfoHasErr) return false;
    const sd = state.organizationObj.statementDescriptor;
    const sdText = sd ? sd : COMPANY_NAME;
    if (!sdText || !validOrgInfo.checkSD(sdText)) {
      bool = false;
      errData.statementDescriptor.msg = err.statementDescriptor;
    }

    if (!validOrgInfo.validUrl(state.organizationObj.organizationUrl)) {
      bool = false;
      errData.organizationUrl.msg = err.organizationUrl;
    }

    if (
      !validOrgInfo.validMinAmount(state.organizationObj.minimumPaymentAmount)
    ) {
      errData.minimumPaymentAmount.msg =
        err.minimumPaymentAmount[0] +
        DEFAULT_PAYMENT_MINIMUM_LIMIT +
        err.minimumPaymentAmount[1];
    }

    if (!state.firstName) {
      bool = false;
      errData.firstName.msg = err.firstNameErr;
    } else {
      errData.firstName.msg = "";
    }

    if (!state.lastName) {
      bool = false;
      errData.lastName.msg = err.lastNameErr;
    } else {
      errData.lastName.msg = "";
    }

    if (!isValidPhoneNumber(state.phoneNumber)) {
      bool = false;
      errData.phoneNumber.msg = err.phoneNumberErr;
    } else {
      errData.phoneNumber.msg = "";
    }

    if (!state.organizationObj.organizationName) {
      bool = false;
      errData.organizationName.msg = err.orgNameErr;
    } else {
      errData.organizationName.msg = "";
    }

    if (!state.organizationObj.subDomain) {
      bool = false;
      errData.subDomain.msg = err.subDomainErr;
    } else {
      errData.subDomain.msg = "";
    }
    func.setState({
      errMsgInfo: errData,
    });

    if (bool) {
      if (state.loading) return false;
      func.setState({
        loading: true,
      });
      simpleFetch("/organizations", "GET", {}).then((res) => {
        // console.log("Save - Current state.organizationObj:", {
        //  defaultLanguage: state.organizationObj.defaultLanguage,
        //  supportedLanguages: state.organizationObj.supportedLanguages,
        //  autoDetectLanguage: state.organizationObj.autoDetectLanguage
        //});

        // Compare current state with backend data and only include changed fields
        const orgDataObj = {};
        const currentData = res.body;

        // Helper function to check if values are different
        const hasChanged = (newVal, oldVal) => {
          if (Array.isArray(newVal) && Array.isArray(oldVal)) {
            return JSON.stringify(newVal) !== JSON.stringify(oldVal);
          }
          return newVal !== oldVal;
        };

        // Only include fields that have changed
        if (hasChanged(state.firstName, currentData.firstName)) {
          orgDataObj.firstName = state.firstName;
        }
        if (hasChanged(state.lastName, currentData.lastName)) {
          orgDataObj.lastName = state.lastName;
        }
        if (hasChanged(state.phoneNumber, currentData.phoneNumber)) {
          orgDataObj.phoneNumber = state.phoneNumber;
        }
        if (hasChanged(state.organizationObj.logo, currentData.organizationObj.logo)) {
          orgDataObj.logo = state.organizationObj.logo;
        }
        if (hasChanged(state.organizationObj.organizationName, currentData.organizationObj.organizationName)) {
          orgDataObj.organizationName = state.organizationObj.organizationName;
        }
        if (hasChanged(state.organizationObj.subDomain, currentData.organizationObj.subDomain)) {
          orgDataObj.subDomain = state.organizationObj.subDomain;
        }
        if (hasChanged(sdText, currentData.organizationObj.statementDescriptor)) {
          orgDataObj.statementDescriptor = sdText;
        }
        if (hasChanged(state.organizationObj.organizationUrl, currentData.organizationObj.organizationUrl)) {
          orgDataObj.organizationUrl = state.organizationObj.organizationUrl;
        }
        if (hasChanged(state.organizationObj.minimumPaymentAmount, currentData.organizationObj.minimumPaymentAmount)) {
          orgDataObj.minimumPaymentAmount = state.organizationObj.minimumPaymentAmount;
        }

        // Only include language settings if they have changed
        if (hasChanged(state.organizationObj.supportedLanguages, currentData.organizationObj.supportedLanguages)) {
          if (state.organizationObj.supportedLanguages && state.organizationObj.supportedLanguages.length > 0) {
            orgDataObj.supportedLanguages = state.organizationObj.supportedLanguages;
          }
        }
        if (hasChanged(state.organizationObj.defaultLanguage, currentData.organizationObj.defaultLanguage)) {
          if (state.organizationObj.defaultLanguage) {
            orgDataObj.defaultLanguage = state.organizationObj.defaultLanguage;
          }
        }
        if (hasChanged(state.organizationObj.autoDetectLanguage, currentData.organizationObj.autoDetectLanguage)) {
          if (state.organizationObj.autoDetectLanguage !== undefined) {
            orgDataObj.autoDetectLanguage = state.organizationObj.autoDetectLanguage;
          }
        }

        if (hasChanged(state.organizationObj.allowedTransactionTypes, currentData.organizationObj.allowedTransactionTypes)) {
          orgDataObj.allowedTransactionTypes = state.organizationObj.allowedTransactionTypes || "both";
        }

        // Handle optional fields
        if (hasChanged(state.organizationObj.organizationAddress, currentData.organizationObj.organizationAddress)) {
          if (state.organizationObj.organizationAddress) {
            orgDataObj.organizationAddress = state.organizationObj.organizationAddress;
          }
        }
        if (hasChanged(state.organizationObj.organizationIRDNo, currentData.organizationObj.organizationIRDNo)) {
          if (state.organizationObj.organizationIRDNo) {
            orgDataObj.organizationIRDNo = state.organizationObj.organizationIRDNo;
          }
        }

        // console.log("Save - Changed fields being sent to backend:", Object.keys(orgDataObj));
        // console.log("Save - Full changed data:", orgDataObj);
        let editOrgData;
        const issetUserNmae = res.body.username !== state.username;
        if (issetUserNmae) {
          orgDataObj.username = state.username;
          orgDataObj.language = func.state.language;
        }

        // Only send request if there are changes
        if (Object.keys(orgDataObj).length === 0) {
          // console.log("Save - No changes detected, skipping update");
          func.setState({
            orgInfoHasErr: true,
            loading: false,
          });
          return;
        }

        editOrgData = simpleFetch("/organizations", "PUT", orgDataObj);
        editOrgData
          .then((res) => {
            func.setState({
              orgInfoHasErr: true,
              loading: false,
            });
            switch (res.status) {
              case 204: {
                break;
              }
              case 200: {
                if (res.body.newUsername && issetUserNmae) {
                  func.props.history.push({
                    pathname: "/UsernameConfirm",
                    confirm: true,
                    nextPage: "/OrgSettings",
                  });
                }
                break;
              }
              case 422: {
                if (res.body.message === "User already exist") {
                  let errData = state.errMsgInfo;
                  errData.username.msg = err.usernameExist;
                  func.setState({
                    errMsgInfo: errData,
                  });
                }
                break;
              }
              default: {
                let errData = state.errMsgInfo;
                errData.submitErr.msg = errorMsg[func.state.language].fail;
                func.setState({
                  errMsgInfo: errData,
                });
              }
            }
          })
          .finally((res) => {
            func.setState({
              validateOrgInfo: true,
            });
          });
      });
    }
  }

  if (state.loading) {
    return (
      <div className="OrgInfo-emptyPage">
        <div className="OrgInfo-center">
          <div className="OrgInfo-btm-load"></div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="OrgInfo-contentContainer">
        <div className="OrgInfo-contentHeader">
          <div className="OrgInfo-info">{textObj.info}</div>

          <div
            className="OrgInfo-submitBtn"
            data-testid="orginfo-save-btn"
            onClick={clickSubmit}
          >
            {textObj.submitBtn}
          </div>
        </div>

        <div className="OrgInfo-content">
          {renderErrorMsg(state.errMsgInfo.submitErr.msg)}

          <div className="OrgInfo-logoUploader">
            <div className="OrgInfo-logoContainer" style={logoStyle}>
              <input type="file" onChange={func.handleLogo} />
              {state.organizationObj.logo
                ? ""
                : textObj.logoContainer + " Logo"}
            </div>
            {renderErrorMsg(state.errMsgInfo.logo.msg)}
          </div>

          {orderOfInputField.map((field) => (
            <RenderInputField
              key={field}
              field={field}
              func={func}
              state={state}
              textObj={textObj}
            />
          ))}

          {PLATFORM === "checkplus" && (
            <RenderAllowedTransactionTypes
              state={state}
              func={func}
              textObj={textObj}
            />
          )}

          <div className="OrgInfo-sdDetails">
            {textObj.sddetails1}
            <br />
            {textObj.sddetails2}
          </div>

          <div className="OrgInfo-footer" />
        </div>
      </div>
    );
  }
}

const RenderInputField = (props) => {
  if (!props) return null;
  const field = props.field;
  if (!field) return null;

  const func = props.func;
  const state = props.state;
  const textObj = props.textObj;
  let inputData = {};
  const subD = state.organizationObj.subDomain;
  const subDomainText =
    subD === ""
      ? textObj.subDomainText[0] + " (YoursubDomain." + MAIN_DOMAIN + ")"
      : textObj.subDomainText[1] + " (" + subD + "." + MAIN_DOMAIN + ")";

  switch (field) {
    case "phoneNumber":
      return (
        <div>
          <div className="OrgInfo-subheading">
            {textObj.orgSubheading[field]}
          </div>
          <div className="OrgInfo-inputFieldContainer">
            <div className="OrgInfo-phoneField">
              <PhoneInput
                placeholder={textObj.orgInputField[field]}
                value={state[field]}
                defaultCountry={"HK"}
                flags={flags}
                onChange={(phone) => {
                  const no = formatPhoneNumberIntl(phone);
                  let errorMsg = state.errMsgInfo;
                  errorMsg.phoneNumber.msg = "";
                  func.setState({
                    phoneNumber: no,
                    errMsgInfo: errorMsg,
                  });
                }}
              />
            </div>
          </div>
          {renderErrorMsg(state.errMsgInfo[field].msg)}
        </div>
      );
    case "defaultLanguage": {
      const currentPlan = state.organizationObj.currentPlan
        ? state.organizationObj.currentPlan.toLowerCase()
        : "free";

      // Determine plan restrictions
      const isProOrPremium = currentPlan === "pro" || currentPlan === "premium";
      const maxLanguages = isProOrPremium ? 3 : 1; // Pro/Premium: multiple, Free/Plus: 1

      const languages = [
        { code: "en", label: "English" },
        { code: "zh", label: "繁體中文 (Traditional Chinese)" },
        { code: "cn", label: "简体中文 (Simplified Chinese)" }
      ];

      // Use supportedLanguages array if available, otherwise fall back to defaultLanguage
      const supportedLanguages = (state.organizationObj.supportedLanguages && state.organizationObj.supportedLanguages.length > 0)
        ? state.organizationObj.supportedLanguages
        : (state.organizationObj.defaultLanguage ? [state.organizationObj.defaultLanguage] : []);

      // Debug logging
      // console.log("OrgSettings Language Debug:");
      // console.log("- state.organizationObj.supportedLanguages:", state.organizationObj.supportedLanguages);
      // console.log("- state.organizationObj.defaultLanguage:", state.organizationObj.defaultLanguage);
      // console.log("- computed supportedLanguages:", supportedLanguages);
      // console.log("- state.organizationObj.currentPlan:", state.organizationObj.currentPlan);
      // console.log("- currentPlan (lowercase):", currentPlan);
      // console.log("- isProOrPremium:", isProOrPremium);
      // console.log("- maxLanguages:", maxLanguages);

      const handleLanguageToggle = (langCode) => {
      // console.log("handleLanguageToggle called with:", langCode);
      // console.log("Current supportedLanguages:", supportedLanguages);
      // console.log("maxLanguages:", maxLanguages);

        let newSupportedLanguages = [...supportedLanguages];

        if (newSupportedLanguages.includes(langCode)) {
        // Remove language
        // console.log("Removing language:", langCode);
          newSupportedLanguages = newSupportedLanguages.filter(code => code !== langCode);
        } else {
        // Add language
        // console.log("Attempting to add language:", langCode);
        // console.log("Current length:", newSupportedLanguages.length, "Max:", maxLanguages);

          if (newSupportedLanguages.length >= maxLanguages) {
            if (maxLanguages === 1) {
            // For free/plus, replace the single language
            // console.log("Replacing single language with:", langCode);
              newSupportedLanguages = [langCode];
            } else {
            // For pro/premium, don't allow more than maxLanguages
            // console.log("Max languages reached!");
              alert(`You can select up to ${maxLanguages} languages with your ${currentPlan} plan.`);
              return;
            }
          } else {
          // console.log("Adding language to array");
            newSupportedLanguages.push(langCode);
          }
        }

        // console.log("New supportedLanguages:", newSupportedLanguages);

        // Update both fields in a single state update to avoid race condition
        // If adding first language or only one language, set it as default
        const newDefaultLanguage = newSupportedLanguages.length === 1
          ? newSupportedLanguages[0]
          : (newSupportedLanguages.includes(state.organizationObj.defaultLanguage)
            ? state.organizationObj.defaultLanguage
            : newSupportedLanguages[0]);

        func.setMultipleOrgInfoData({
          supportedLanguages: newSupportedLanguages,
          defaultLanguage: newSupportedLanguages.length > 0 ? newDefaultLanguage : null
        });
      };

      return (
        <div>
          <div className="OrgInfo-subheading">
            {textObj.orgSubheading[field] || "Supported Languages for Payment Pages"}
          </div>

          {/* Plan information box */}
          <div style={{
            marginTop: "10px",
            marginBottom: "15px",
            padding: "12px 15px",
            backgroundColor: isProOrPremium ? "#f0f8ff" : "#fff9e6",
            border: `1px solid ${isProOrPremium ? "#4A9FE9" : "#ffd700"}`,
            borderRadius: "6px",
            fontSize: "13px",
            color: "#333",
            lineHeight: "1.6"
          }}>
            <div style={{ fontWeight: "600", marginBottom: "5px" }}>
              {isProOrPremium ? "💎 Pro/Premium Plan" : "🆓 Free/Plus Plan"}
            </div>
            <div>
              {isProOrPremium
                ? `You can select up to ${maxLanguages} languages. Visitors will see a language selector if you choose multiple languages.`
                : "You can select one language. Upgrade to Pro or Premium for multi-language support."
              }
            </div>
          </div>

          {/* Language detection toggle */}
          {supportedLanguages.length > 0 && (
            <div style={{
              marginBottom: "12px",
              padding: "12px 15px",
              backgroundColor: "#f9f9f9",
              border: "1px solid #e0e0e0",
              borderRadius: "6px"
            }}>
              <label style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                fontSize: "14px"
              }}>
                <input
                  type="checkbox"
                  checked={state.organizationObj.autoDetectLanguage !== false}
                  onChange={(e) => {
                    func.handleOrgInput("autoDetectLanguage", {
                      target: { value: e.target.checked }
                    });
                  }}
                  style={{
                    marginRight: "10px",
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                    accentColor: "#4A9FE9"
                  }}
                />
                <div>
                  <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                  Auto-detect visitor's language
                  </div>
                  <div style={{ fontSize: "12px", color: "#666", lineHeight: "1.4" }}>
                    {supportedLanguages.length === 1
                      ? "When unchecked, always use your selected language. When checked, use visitor's browser language if it matches your selection."
                      : "When checked, visitors see their browser language if it's in your list, otherwise the default (first) language. When unchecked, always show the default language."
                    }
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Language selection note for multi-language */}
          {supportedLanguages.length > 1 && (
            <div style={{
              marginBottom: "12px",
              padding: "10px 12px",
              backgroundColor: "#fffbf0",
              border: "1px solid #ffd700",
              borderRadius: "4px",
              fontSize: "12px",
              color: "#666"
            }}>
              <strong>Default Language:</strong> Click "Set as Default" to choose which language shows when a visitor's browser language isn't in your list.
            </div>
          )}

          {/* Checkbox options */}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {languages.map((lang) => {
              const isChecked = supportedLanguages.includes(lang.code);
              const isDefault = state.organizationObj.defaultLanguage === lang.code;

              return (
                <div
                  key={lang.code}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      padding: "14px 16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      backgroundColor: isChecked ? "#f0f8ff" : "#ffffff",
                      borderColor: isChecked ? "#4A9FE9" : "#e0e0e0",
                      transition: "all 0.2s ease",
                      fontSize: "14px",
                      minHeight: "50px",
                      cursor: "pointer"
                    }}
                    onMouseEnter={(e) => {
                      if (!isChecked) {
                        e.currentTarget.style.backgroundColor = "#f9f9f9";
                        e.currentTarget.style.borderColor = "#c0c0c0";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isChecked) {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.borderColor = "#e0e0e0";
                      }
                    }}
                    onClick={() => handleLanguageToggle(lang.code)}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}} // Handled by div onClick
                      style={{
                        marginRight: "14px",
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        accentColor: "#4A9FE9",
                        flexShrink: 0,
                        pointerEvents: "none" // Prevent double-triggering
                      }}
                    />
                    <span style={{
                      flex: 1,
                      lineHeight: "1.4",
                      wordBreak: "break-word",
                      userSelect: "none"
                    }}>
                      {lang.label}
                      {isChecked && isDefault && (
                        <span style={{
                          marginLeft: "8px",
                          fontSize: "11px",
                          color: "#4A9FE9",
                          fontWeight: "600"
                        }}>
                        ⭐ Default
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Set as Default button for checked languages */}
                  {isChecked && !isDefault && supportedLanguages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        func.setMultipleOrgInfoData({
                          defaultLanguage: lang.code
                        });
                      }}
                      style={{
                        padding: "8px 12px",
                        fontSize: "12px",
                        border: "1px solid #4A9FE9",
                        borderRadius: "4px",
                        backgroundColor: "#ffffff",
                        color: "#4A9FE9",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        fontWeight: "500"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#4A9FE9";
                        e.currentTarget.style.color = "#ffffff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.color = "#4A9FE9";
                      }}
                      title="Set as default language"
                    >
                    Set as Default
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Help text */}
          <div style={{
            marginTop: "12px",
            fontSize: "13px",
            color: "#666",
            lineHeight: "1.6",
            padding: "8px 0"
          }}>
            {supportedLanguages.length === 0 ? (
              <span>No languages selected. Visitors will see content in their browser's default language.</span>
            ) : supportedLanguages.length === 1 ? (
              <span>Selected language will be used for all visitors.</span>
            ) : (
              <span>
                <strong>Default language:</strong> Shows to visitors whose browser language isn't in your list.{" "}
                <strong>Auto-detect:</strong> Visitors whose browser language matches your list will see their preferred language.
              </span>
            )}
          </div>
        </div>
      );
    }
    case "email":
      return (
        <div>
          <div className="OrgInfo-subheading">
            {textObj.orgSubheading[field]}
          </div>
          <Textbox
            id={"email"}
            name="email"
            type="text"
            placeholder={textObj.orgInputField[field]}
            onChange={func.handleUsername}
            value={state.username}
            classNameInput="OrgInfo-inputField"
            classNameContainer="OrgInfo-inputFieldContainer"
            validate={state.validateOrgInfo}
            validationCallback={(res) => {
              func.setState({
                orgInfoHasErr: res,
                validateOrgInfo: false,
              });
            }}
            onBlur={(e) => {}}
            validationOption={validOrgInfo.username(
              state.errMsgInfo.username.msg
            )}
          />
        </div>
      );
    case "statementDescriptor":
      try {
        inputData.subheading = textObj.orgSubheading[field];
        inputData.value = state.organizationObj[field];
        inputData.err = state.errMsgInfo[field].msg;
        inputData.onChange = func.handleOrgInput.bind(func, field);
        inputData.maxLength = "10";
      } catch (e) {
        return null;
      }
      break;

    case "subDomain":
      try {
        inputData.subheading = subDomainText;
        inputData.value = state.organizationObj[field];
        inputData.err = state.errMsgInfo.subDomain.msg;
        inputData.onChange = func.handleOrgInput.bind(func, field);
      } catch (e) {
        return null;
      }
      break;
    case "firstName":
    case "lastName":
      try {
        inputData.subheading = textObj.orgSubheading[field];
        inputData.value = state[field];
        inputData.err = state.errMsgInfo[field].msg;
        inputData.onChange = func.handleOrgDataInput.bind(func, field);
      } catch (e) {
        return null;
      }
      break;
    default:
      try {
        // Platform-specific label for organizationIRDNo
        if (field === "organizationIRDNo" && PLATFORM === "plug") {
          inputData.subheading = "Business Registration (BR) No.";
        } else {
          inputData.subheading = textObj.orgSubheading[field];
        }
        inputData.value = state.organizationObj[field];
        inputData.err = state.errMsgInfo[field].msg;
        inputData.onChange = func.handleOrgInput.bind(func, field);
      } catch (e) {
        return null;
      }
  }

  return (
    <div>
      <div className="OrgInfo-subheading">{inputData.subheading}</div>
      <div className="OrgInfo-inputFieldContainer">
        <input
          type="text"
          className="OrgInfo-inputField"
          data-testid={`orginfo-input-${field}`}
          placeholder={
            field === "organizationIRDNo" && PLATFORM === "plug"
              ? "Please enter Business Registration (BR) No."
              : textObj.orgInputField[field]
          }
          onChange={inputData.onChange}
          value={inputData.value}
          maxLength={inputData.maxLength}
        />
      </div>
      {renderErrorMsg(inputData.err)}
    </div>
  );
};

RenderInputField.propTypes = {
  field: PropTypes.string,
  func: PropTypes.object,
  textObj: PropTypes.object,
  state: PropTypes.object,
};

const RenderAllowedTransactionTypes = ({ state, func, textObj }) => {
  const current = state.organizationObj.allowedTransactionTypes || "both";
  const attText = textObj.allowedTransactionTypes || {};
  const label = attText.label || "Allowed Transaction Types";

  const options = [
    { value: "both", label: attText.both || "Both (Donation & Payment)" },
    { value: "donation", label: attText.donationOnly || "Donation Only" },
    { value: "payment", label: attText.paymentOnly || "Payment Only" },
  ];

  return (
    <div>
      <div className="OrgInfo-subheading">{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
        {options.map((opt) => (
          <div
            key={opt.value}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              border: "1px solid " + (current === opt.value ? "#4A9FE9" : "#e0e0e0"),
              borderRadius: "6px",
              backgroundColor: current === opt.value ? "#f0f8ff" : "#ffffff",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onClick={() => func.handleOrgInput("allowedTransactionTypes", { target: { value: opt.value } })}
          >
            <input
              type="radio"
              name="allowedTransactionTypes"
              checked={current === opt.value}
              onChange={() => {}}
              style={{ marginRight: "12px", accentColor: "#4A9FE9", pointerEvents: "none" }}
            />
            <span style={{ fontSize: "14px" }}>{opt.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

RenderAllowedTransactionTypes.propTypes = {
  state: PropTypes.shape({
    organizationObj: PropTypes.shape({
      allowedTransactionTypes: PropTypes.string,
    }),
  }),
  func: PropTypes.shape({
    handleOrgInput: PropTypes.func,
  }),
  textObj: PropTypes.shape({
    allowedTransactionTypes: PropTypes.object,
  }),
};
